import type {
  ContentNode,
  ContentField,
  ScoreField,
  SelectField,
  CheckboxListField,
  StatementListField,
  GridField,
  ScoreOption,
  ScoreGroupNode,
} from "@/types/template";
import type { ParseResult } from "@/parser/types";
import { parseFrontmatter } from "@/parser/parseFrontmatter";
import { parseFieldParams } from "@/parser/parseFieldParams";

// Re-export types so existing consumers don't need to change imports
export type { ParseError, ParseResult } from "@/parser/types";

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/^-|-$/g, "");
}

interface ParserState {
  contentNodes: ContentNode[];
  slugCounts: Map<string, number>;
  textLines: string[];
  currentField: Partial<ContentField> | null;
  awaitingLabel: boolean;
  currentScoregroupId: string | null;
  errors: ParseResult["errors"];
}

function makeSlug(text: string, slugCounts: Map<string, number>): string {
  const base = slugify(text);
  const count = slugCounts.get(base) ?? 0;
  const slug = count === 0 ? base : `${base}-${count + 1}`;
  slugCounts.set(base, count + 1);
  return slug;
}

function flushText(state: ParserState): void {
  if (state.textLines.length > 0) {
    state.contentNodes.push({ markdown: state.textLines.join("\n"), type: "text" });
    state.textLines = [];
  }
}

function finishField(state: ParserState): void {
  if (state.currentField?.id && state.currentField.label !== undefined) {
    state.contentNodes.push({ field: state.currentField as ContentField, type: "field" });
  }
  state.currentField = null;
  state.awaitingLabel = false;
}

function parseSectionInner(inner: string, state: ParserState): void {
  const hasRequired = /,\s*required\s*$/.test(inner);
  const titlePart = hasRequired ? inner.replace(/,\s*required\s*$/, "") : inner;
  const titleMatch = /^title=(.+)/.exec(titlePart);
  const title = titleMatch ? (titleMatch[1] ?? "").trim() : titlePart.trim();
  const id = makeSlug(title, state.slugCounts);
  state.contentNodes.push({ id, required: hasRequired, title, type: "section" });
}

function parseScoreOptionLine(content: string, currentField: Partial<ContentField>): boolean {
  const scoreOptMatch = /^\[(\d+)\]\s+(.+)/.exec(content);
  if (!scoreOptMatch) {
    return false;
  }
  const value = Number.parseInt(scoreOptMatch[1] ?? "0");
  const label = scoreOptMatch[2] ?? "";
  const sf = currentField as Partial<ScoreField>;
  sf.scoreOptions ??= [];
  sf.scoreOptions.push({ label, value } as ScoreOption);
  return true;
}

function parseGridLine(content: string, currentField: Partial<ContentField>): boolean {
  if (!content.startsWith("|")) {
    return false;
  }
  const cells = content
    .split("|")
    .slice(1, -1)
    .map((c) => c.trim());
  const gf = currentField as Partial<GridField>;
  gf.columns ??= [];
  gf.rows ??= [];
  if (gf.columns.length === 0) {
    gf.columns = cells.slice(1);
  } else {
    appendGridRow(cells, gf);
  }
  return true;
}

function appendGridRow(cells: string[], gf: Partial<GridField>): void {
  const rowLabel = cells[0] ?? "";
  const defaults: Record<string, string> = {};
  let hasDefaults = false;
  for (let c = 1; c < cells.length && c - 1 < (gf.columns?.length ?? 0); c++) {
    const cellVal = cells[c];
    const colKey = gf.columns?.[c - 1];
    if (cellVal && colKey !== undefined) {
      defaults[colKey] = cellVal;
      hasDefaults = true;
    }
  }
  gf.rows?.push({ label: rowLabel, ...(hasDefaults ? { defaults } : {}) });
}

function appendListItemToField(content: string, currentField: Partial<ContentField>): void {
  switch (currentField.type) {
    case "select": {
      const sf = currentField as Partial<SelectField>;
      sf.options = [...(sf.options ?? []), content];
      break;
    }
    case "checkbox-list": {
      const cf = currentField as Partial<CheckboxListField>;
      cf.items = [...(cf.items ?? []), content];
      break;
    }
    case "statement-list": {
      const slField = currentField as Partial<StatementListField>;
      slField.items = [...(slField.items ?? []), content];
      break;
    }
    default: {
      break;
    }
  }
}

function processFieldBodyLine(content: string, state: ParserState): void {
  const field = state.currentField;
  if (!field) {
    return;
  }
  if (field.type === "score" && parseScoreOptionLine(content, field)) {
    return;
  }
  if (field.type === "grid" && parseGridLine(content, field)) {
    return;
  }
  appendListItemToField(content, field);
}

function tryProcessDirective(trimmed: string, lineNum: number, state: ParserState): boolean {
  const sectionMatch = /^@section\((.+)\)\s*$/.exec(trimmed);
  if (sectionMatch) {
    finishField(state);
    flushText(state);
    state.currentScoregroupId = null;
    parseSectionInner(sectionMatch[1] ?? "", state);
    return true;
  }

  const headingMatch = /^(#{1,3})\s+(.+)/.exec(trimmed);
  if (headingMatch) {
    finishField(state);
    flushText(state);
    const level = (headingMatch[1] ?? "").length;
    const text = (headingMatch[2] ?? "").trim();
    state.contentNodes.push({ level, text, type: "heading" });
    return true;
  }

  const scoregroupMatch = /^@scoregroup\(([^)]+)\)/.exec(trimmed);
  if (scoregroupMatch) {
    finishField(state);
    flushText(state);
    const id = (scoregroupMatch[1] ?? "").trim();
    state.currentScoregroupId = id;
    state.contentNodes.push({ id, type: "scoregroup" } as ScoreGroupNode);
    return true;
  }

  return tryProcessFieldDirective(trimmed, lineNum, state);
}

function tryProcessFieldDirective(trimmed: string, lineNum: number, state: ParserState): boolean {
  const fieldDirectiveMatch = /^@field\(([^)]+)\)/.exec(trimmed);
  if (!fieldDirectiveMatch) {
    return false;
  }
  finishField(state);
  flushText(state);
  const rawParams = (fieldDirectiveMatch[1] ?? "").split(",").map((p) => p.trim());
  const typeStr = rawParams[0] ?? "";
  const extraParams = rawParams.slice(1);
  state.currentField = parseFieldParams(typeStr, extraParams, lineNum, state.errors);
  if (state.currentField?.type === "score" && state.currentScoregroupId) {
    (state.currentField as Partial<ScoreField>).scoregroupId = state.currentScoregroupId;
  }
  state.awaitingLabel = true;
  return true;
}

function processLineTrimmed(
  trimmed: string,
  lineNum: number,
  indented: boolean,
  state: ParserState,
): boolean {
  if (tryProcessDirective(trimmed, lineNum, state)) {
    return true;
  }

  if (state.awaitingLabel && state.currentField) {
    if (!indented) {
      state.currentField.label = trimmed;
      state.currentField.id = makeSlug(trimmed, state.slugCounts);
      state.awaitingLabel = false;
    }
    return true;
  }

  if (state.currentField && !state.awaitingLabel && indented) {
    processFieldBodyLine(trimmed, state);
    return true;
  }

  if (state.currentField && !state.awaitingLabel && !indented) {
    finishField(state);
  }

  return false;
}

export function parseTemplate(source: string): ParseResult {
  const errors: ParseResult["errors"] = [];
  const { bodyContent, frontmatter } = parseFrontmatter(source, errors);

  const lines = bodyContent.split("\n");
  if (lines.length > 0 && lines.at(-1) === "") {
    lines.pop();
  }

  const state: ParserState = {
    awaitingLabel: false,
    contentNodes: [],
    currentField: null,
    currentScoregroupId: null,
    errors,
    slugCounts: new Map<string, number>(),
    textLines: [],
  };

  for (const [i, line] of lines.entries()) {
    const lineNum = i + 1;
    const trimmed = line.trim();
    const indented = line.length > 0 && (line.startsWith(" ") || line.startsWith("\t"));

    // Blank line
    if (trimmed === "") {
      if (state.currentField && !state.awaitingLabel) {
        finishField(state);
      }
      state.textLines.push("");
      continue;
    }

    const handled = processLineTrimmed(trimmed, lineNum, indented, state);
    if (!handled) {
      state.textLines.push(line);
    }
  }

  finishField(state);
  flushText(state);

  return {
    content: {
      category: frontmatter.category,
      content: state.contentNodes,
      isSubTemplate: frontmatter.isSubTemplate,
      name: frontmatter.name,
      variables: frontmatter.variables,
    },
    errors,
  };
}
